
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import GmailAuth from "@/components/email/GmailAuth";
import Microsoft365Auth from "@/components/email/Microsoft365Auth";
import { useQueryClient } from "@tanstack/react-query";
import { ImapForm } from "./ImapForm";

interface AddEmailAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AddEmailAccountDialog = ({ open, onOpenChange }: AddEmailAccountDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [accountName, setAccountName] = useState("");

  const handleGmailSuccess = async (email: string, credentials: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      // Check if account already exists
      const { data: existingAccounts, error: queryError } = await supabase
        .from('email_accounts')
        .select('*')
        .eq('email', email);

      if (queryError) throw queryError;

      if (existingAccounts && existingAccounts.length > 0) {
        // Close dialog, refresh data, and show toast
        onOpenChange(false);
        await queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
        
        toast({
          title: "Account Already Connected",
          description: "This Gmail account is already in your connected accounts.",
        });
        return;
      }

      // If account doesn't exist, proceed with adding it
      await queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
      onOpenChange(false);
      toast({
        title: "Account Added",
        description: "Gmail account has been connected successfully",
      });
    } catch (error) {
      console.error('Error saving Gmail account:', error);
      toast({
        title: "Error",
        description: "Failed to save Gmail account",
        variant: "destructive",
      });
    }
  };

  const handleMicrosoft365Success = async (email: string, credentials: any) => {
    try {
      await queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
      onOpenChange(false);
      toast({
        title: "Account Added",
        description: "Microsoft 365 account has been connected successfully",
      });
    } catch (error) {
      console.error('Error saving Microsoft 365 account:', error);
      toast({
        title: "Error",
        description: "Failed to save Microsoft 365 account",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Email Account</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="gmail" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="gmail">Gmail</TabsTrigger>
            <TabsTrigger value="microsoft365">Microsoft 365</TabsTrigger>
            <TabsTrigger value="imap">IMAP/Exchange</TabsTrigger>
          </TabsList>
          <TabsContent value="gmail" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="accountName">Account Name (Optional)</Label>
                <Input
                  id="accountName"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="My Work Email"
                />
              </div>
              <GmailAuth 
                onSuccess={handleGmailSuccess} 
                accountName={accountName}
              />
            </div>
          </TabsContent>
          <TabsContent value="microsoft365" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="ms365AccountName">Account Name (Optional)</Label>
                <Input
                  id="ms365AccountName"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="My Work Email"
                />
              </div>
              <Microsoft365Auth 
                onSuccess={handleMicrosoft365Success} 
                accountName={accountName}
              />
            </div>
          </TabsContent>
          <TabsContent value="imap">
            <ImapForm 
              accountName={accountName}
              setAccountName={setAccountName}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
                onOpenChange(false);
              }}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmailAccountDialog;
